// prettier-ignore
const HTML_ATTRIBUTES = new Set([
    'accept', 'acceptCharset', 'accessKey', 'action', 'allowFullScreen', 'allowTransparency',
    'alt', 'async', 'autoComplete', 'autoFocus', 'autoPlay', 'capture', 'cellPadding', 
    'cellSpacing', 'challenge', 'charSet', 'checked', 'classID', 'className', 'colSpan', 
    'cols', 'content', 'contentEditable', 'contextMenu', 'controls', 'coords', 'crossOrigin', 
    'data', 'dateTime', 'default', 'defer', 'dir', 'disabled', 'download', 'draggable',
    'encType', 'form', 'formAction', 'formEncType', 'formMethod', 'formNoValidate', 
    'formTarget', 'frameBorder', 'headers', 'height', 'hidden', 'high', 'href', 'hrefLang', 
    'htmlFor', 'httpEquiv', 'icon', 'id', 'inputMode', 'integrity', 'is', 'keyParams', 
    'keyType', 'kind', 'label', 'lang', 'list', 'loop', 'low', 'manifest', 'marginHeight', 
    'marginWidth', 'max', 'maxLength', 'media', 'mediaGroup', 'method', 'min', 'minLength', 
    'multiple', 'muted', 'name', 'noValidate', 'nonce', 'open', 'optimum', 'pattern', 
    'placeholder', 'poster', 'preload', 'radioGroup', 'readOnly', 'rel', 'required',
    'reversed', 'role', 'rowSpan', 'rows', 'sandbox', 'scope', 'scoped', 'scrolling', 
    'seamless', 'selected', 'shape', 'size', 'sizes', 'span', 'spellCheck', 'src', 
    'srcDoc', 'srcLang', 'srcSet', 'start', 'step', 'style', 'summary', 'tabIndex', 
    'target', 'title', 'type', 'useMap', 'value', 'width', 'wmode', 'wrap'
  ]);

// prettier-ignore
const EVENT_HANDLERS = new Set([
    'onCopy', 'onCut', 'onPaste', 'onCompositionEnd', 'onCompositionStart', 'onCompositionUpdate', 
    'onKeyDown', 'onKeyPress', 'onKeyUp', 'onFocus', 'onBlur', 'onChange', 'onInput', 'onSubmit', 
    'onClick', 'onContextMenu', 'onDoubleClick', 'onDrag', 'onDragEnd', 'onDragEnter', 'onDragExit', 
    'onDragLeave', 'onDragOver', 'onDragStart', 'onDrop', 'onMouseDown', 'onMouseEnter', 
    'onMouseLeave', 'onMouseMove', 'onMouseOut', 'onMouseOver', 'onMouseUp', 'onSelect', 
    'onTouchCancel', 'onTouchEnd', 'onTouchMove', 'onTouchStart', 'onScroll', 'onWheel', 
    'onAbort', 'onCanPlay', 'onCanPlayThrough', 'onDurationChange', 'onEmptied', 'onEncrypted', 
    'onEnded', 'onError', 'onLoadedData', 'onLoadedMetadata', 'onLoadStart', 'onPause', 
    'onPlay', 'onPlaying', 'onProgress', 'onRateChange', 'onSeeked', 'onSeeking', 'onStalled', 
    'onSuspend', 'onTimeUpdate', 'onVolumeChange', 'onWaiting', 'onLoad'
  ]);

const ARIA_PREFIX = 'aria-';
const DATA_PREFIX = 'data-';

export type PickConfig = {
    aria?: boolean;
    data?: boolean;
    attr?: boolean;
    events?: boolean;
};

type PropObject = Record<string, any>;

const isAriaAttribute = (key: string) => key === 'role' || key.startsWith(ARIA_PREFIX);

const isDataAttribute = (key: string) => key.startsWith(DATA_PREFIX);

const isValidHtmlAttribute = (key: string) => HTML_ATTRIBUTES.has(key);

const isEventHandler = (key: string) => EVENT_HANDLERS.has(key);

/**
 * Extracts DOM-safe attributes from props object
 * @param config - Configuration for which attributes to pick
 * @returns New object with filtered attributes
 */
export function pickAttrs<T extends PropObject>(
    props: T,
    config: boolean | PickConfig = false
): Partial<T> {
    // Normalize config parameter
    let normalizedConfig: Required<PickConfig>;

    if (config === false) {
        normalizedConfig = { aria: true, data: true, attr: true, events: true };
    } else if (config === true) {
        normalizedConfig = { aria: true, data: false, attr: false, events: false };
    } else {
        normalizedConfig = {
            aria: config.aria ?? false,
            data: config.data ?? false,
            attr: config.attr ?? false,
            events: config.events ?? false
        };
    }

    const result: Partial<T> = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(props)) {
        const shouldInclude =
            (normalizedConfig.aria && isAriaAttribute(key)) ||
            (normalizedConfig.data && isDataAttribute(key)) ||
            (normalizedConfig.attr && isValidHtmlAttribute(key)) ||
            (normalizedConfig.events && isEventHandler(key));

        if (shouldInclude) {
            result[key as keyof T] = value;
        }
    }

    return result;
}
